import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { MenuService } from '../services/menu.service';
import { toPrincipalId } from '../config/approval-flow.constants';
import { DIACRITICS } from '../utils/text.utils';
import { environment } from '../../../environments/environment';
import { PUBLIC_REQUEST } from '../config/http-context.tokens';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    // APIs públicos de terceros (sismos, clima): ninguna credencial ni cabecera de la app.
    if (req.context.get(PUBLIC_REQUEST)) {
        return next(req);
    }

    // La evaluación de pedidos vive dentro de ROE pero el negocio la dio de alta como
    // consumidor aparte, con su propia llave. Va antes que la rama de ROE porque su URL
    // también empieza por el prefijo de ROE.
    if (isOrderEvaluationRequest(req.url)) {
        return next(req.clone({ setHeaders: { 'X-Api-Key': environment.orderEvaluationApiKey } }));
    }

    // ROE (OfferEngine) no es Identity: se autentica solo con su X-Api-Key. Ni el JWT le
    // corresponde ni su 401 (llave inválida) significa sesión vencida.
    if (isOfferEngineRequest(req.url)) {
        return next(req.clone({ setHeaders: { 'X-Api-Key': environment.offerEngineApiKey } }));
    }

    // SBL Core (cronograma, sucursales y pedidos automáticos): igual que ROE, solo llave.
    if (isSblCoreRequest(req.url)) {
        return next(req.clone({ setHeaders: { 'X-Api-Key': environment.sblCoreApiKey } }));
    }

    // Salesforce Marketing Cloud (sincronización de productos e inventario): también se autentica
    // solo con su llave. Ni el JWT de Identity le corresponde ni su 401 (llave inválida) significa
    // sesión vencida: no debe abrir el refresh ni cerrar la sesión.
    if (isSfmcRequest(req.url)) {
        return next(req.clone({ setHeaders: { 'X-Api-Key': environment.sfmcApiKey } }));
    }

    // Vitamins (e-commerce): cuelga del mismo origen que el API de la app, pero con su propia
    // llave. Va antes de la rama general, que le pondría el JWT y reaccionaría a su 401.
    if (isVitaminsRequest(req.url)) {
        return next(req.clone({ setHeaders: { 'X-Api-Key': environment.vitaminsApiKey } }));
    }

    // El API de aprobación tampoco es Identity, pero necesita DOS cabeceras: la llave
    // identifica al sistema y `X-Acting-User` a la persona, que es de donde el API deduce
    // el rol (solicitante o aprobador). Sus 401/403 son de llave o de rol, nunca sesión
    // vencida, así que no deben disparar el refresco del token.
    if (isApprovalWorkflowRequest(req.url)) {
        return next(req.clone({ setHeaders: approvalWorkflowHeaders(inject(AuthService)) }));
    }

    const authService = inject(AuthService);
    const menuService = inject(MenuService);
    const router = inject(Router);
    const isAuthLoginRequest = req.url.includes('/identity/auth/login');
    const isAuthRefreshRequest = req.url.includes('/identity/auth/refresh');

    let headers = req.headers;

    // Anti-cache headers for GET requests to prevent stale data
    if (req.method === 'GET') {
        headers = headers
            .set('Cache-Control', 'no-cache, no-store, must-revalidate')
            .set('Pragma', 'no-cache')
            .set('Expires', '0');
    }

    // Add environment ID header
    if (environment.environmentId) {
        headers = headers.set('X-Environment-ID', environment.environmentId);
    }

    // Add Bearer token if present and not already set
    const token = authService.getToken();
    if (token && !headers.has('Authorization') && !isAuthLoginRequest && !isAuthRefreshRequest) {
        headers = headers.set('Authorization', `Bearer ${token}`);
    }

    const isAuthenticatedRequest =
        headers.has('Authorization') && !isAuthLoginRequest && !isAuthRefreshRequest;

    if (isAuthenticatedRequest && isConfiguredApiOriginRequest(req.url)) {
        const currentPageId = menuService.getCurrentPageId();
        if (isValidPageId(currentPageId)) {
            headers = headers.set('X-Page-ID', String(currentPageId));
        }
    }

    const clonedReq = req.clone({ headers });

    return next(clonedReq).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 401 && !isAuthLoginRequest && !isAuthRefreshRequest) {
                return handle401Error(clonedReq, next, authService, router);
            }

            return throwError(() => error);
        })
    );
};

function handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthService,
    router: Router
): Observable<HttpEvent<unknown>> {
    if (authService.startTokenRefresh()) {
        return authService.refreshAuthToken().pipe(
            switchMap((success) => {
                if (success) {
                    const newToken = authService.getToken();
                    authService.completeTokenRefresh(newToken);
                    const retryReq = request.clone({
                        headers: request.headers.set('Authorization', `Bearer ${newToken}`),
                    });
                    return next(retryReq);
                }

                authService.completeTokenRefresh(null);
                authService.logout();
                router.navigate(['/login']);
                return EMPTY;
            }),
            catchError(() => {
                authService.completeTokenRefresh(null);
                authService.logout();
                router.navigate(['/login']);
                return EMPTY;
            })
        );
    }

    // Another request is already refreshing → wait for the new token
    return authService.waitForTokenRefresh().pipe(
        switchMap((token) => {
            const retryReq = request.clone({
                headers: request.headers.set('Authorization', `Bearer ${token}`),
            });
            return next(retryReq);
        })
    );
}

function isOfferEngineRequest(url: string): boolean {
    const prefix = environment.offerEngineApiUrl;
    return url === prefix || url.startsWith(`${prefix}/`);
}

/** La evaluación de pedidos: `…/roe/order-evaluations`, con su propia llave. */
function isOrderEvaluationRequest(url: string): boolean {
    return url.startsWith(`${environment.offerEngineApiUrl}/order-evaluations`);
}

function isSblCoreRequest(url: string): boolean {
    const prefix = environment.sblCoreApiUrl;
    return url === prefix || url.startsWith(`${prefix}/`);
}

function isSfmcRequest(url: string): boolean {
    const prefix = environment.sfmcApiUrl;
    return url === prefix || url.startsWith(`${prefix}/`);
}

function isVitaminsRequest(url: string): boolean {
    const prefix = environment.vitaminsApiUrl;
    return url === prefix || url.startsWith(`${prefix}/`);
}

function isApprovalWorkflowRequest(url: string): boolean {
    const prefix = environment.approvalWorkflowApiUrl;
    return url === prefix || url.startsWith(`${prefix}/`);
}

/**
 * Cabeceras del API de aprobación. El `principalId` se normaliza a minúsculas porque
 * el API lo hace de todos modos, y así el valor que se envía es el que queda grabado.
 */
function approvalWorkflowHeaders(authService: AuthService): Record<string, string> {
    const headers: Record<string, string> = {
        'X-Api-Key': environment.approvalWorkflowApiKey,
    };

    const user = authService.currentUser();
    const email = String(user?.email ?? '').trim();
    if (email) {
        headers['X-Acting-User'] = toPrincipalId(email);
    }

    const displayName = toHeaderSafeText(String(user?.name ?? '').trim());
    if (displayName) {
        headers['X-Acting-User-Name'] = displayName;
    }

    return headers;
}

/**
 * Deja el nombre en ASCII imprimible para que pueda viajar en una cabecera.
 *
 * No basta con ISO-8859-1: el navegador codifica el valor en UTF-8 y Kestrel responde
 * **400 sin cuerpo y sin cabeceras CORS** en cuanto ve un byte no ASCII, que el navegador
 * reporta como un error de CORS y despista por completo. Pasó con «Melquiades García».
 *
 * Los acentos se transliteran (García → Garcia) en vez de borrarse, para que el nombre
 * siga siendo legible en el historial; el resto se descarta, porque el nombre es
 * decorativo y no vale la pena tumbar la llamada por él.
 */
function toHeaderSafeText(value: string): string {
    return (
        value
            .normalize('NFD')
            .replace(DIACRITICS, '')
            .replace(/[^\x20-\x7E]/g, '')
            .trim()
    );
}

function isConfiguredApiOriginRequest(url: string): boolean {
    const requestOrigin = resolveUrlOrigin(url);
    const apiOrigin = resolveUrlOrigin(environment.identityApiUrl);

    return !!requestOrigin && !!apiOrigin && requestOrigin === apiOrigin;
}

function resolveUrlOrigin(url: string): string | null {
    try {
        const baseUrl =
            typeof window !== 'undefined' && window.location?.origin
                ? window.location.origin
                : environment.identityApiUrl;

        return new URL(url, baseUrl).origin;
    } catch {
        return null;
    }
}

function isValidPageId(pageId: number | null): pageId is number {
    return typeof pageId === 'number' && Number.isInteger(pageId) && pageId > 0;
}
